import React, { useEffect, useRef, useState } from 'react'
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormHelperText,
  Typography,
} from '@mui/material'
import { Vehicle } from '@otw/models'
import { usePendingSaleOverrideSettings } from '@/hooks/admin/usePendingSaleOverrideSettings'
import { PendingSaleOverrideConsumeError } from '@/hooks/admin/pendingSaleOverrideCode'
import { useSnackbar } from '@/hooks/common/useSnackbar'

export interface PendingSaleNotPurchasedDialogProps {
  open: boolean
  onClose: () => void
  /** Vehicles that are not purchased for inventory and triggered the gate. */
  vehicles: Array<
    Pick<Vehicle, 'id' | 'stock_number' | 'pending_sale_not_purchased_notify'>
  >
  onUnlocked: () => void
  /** Persist the notify flag on each vehicle (false → true triggers Functions). */
  onNotify: (
    vehicles: Array<Pick<Vehicle, 'id' | 'stock_number' | 'pending_sale_not_purchased_notify'>>,
  ) => Promise<void>
}

const CODE_LENGTH = 6
const CODE_CHAR_PATTERN = /^[A-Z0-9]$/

function sanitizeCodeChars(value: string): string[] {
  return value
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, '')
    .slice(0, CODE_LENGTH)
    .split('')
}

interface OverrideCodeBoxesProps {
  value: string
  onChange: (next: string) => void
  disabled?: boolean
  error?: boolean
  /** Incremented when the parent dialog finishes opening so the first box can take focus. */
  focusRequestId?: number
  onCompleteEnter?: () => void
}

const OverrideCodeBoxes: React.FC<OverrideCodeBoxesProps> = ({
  value,
  onChange,
  disabled = false,
  error = false,
  focusRequestId = 0,
  onCompleteEnter,
}) => {
  const digits = Array.from({ length: CODE_LENGTH }, (_, index) => value[index] || '')
  const inputRefs = useRef<Array<HTMLInputElement | null>>([])

  const focusIndex = (index: number) => {
    const clamped = Math.max(0, Math.min(CODE_LENGTH - 1, index))
    const input = inputRefs.current[clamped]
    if (!input || input.disabled) return
    input.focus({ preventScroll: true })
    input.select()
  }

  useEffect(() => {
    if (focusRequestId <= 0 || disabled) return
    // Dialog focus trap settles after enter — focus on next frame.
    const frame = window.requestAnimationFrame(() => {
      focusIndex(0)
    })
    return () => window.cancelAnimationFrame(frame)
  }, [focusRequestId, disabled])

  const commitDigits = (nextDigits: string[]) => {
    const padded = Array.from({ length: CODE_LENGTH }, (_, index) => nextDigits[index] || '')
    onChange(padded.join(''))
  }

  const handleChange = (index: number, raw: string) => {
    const chars = sanitizeCodeChars(raw)
    if (chars.length === 0) {
      const next = [...digits]
      next[index] = ''
      commitDigits(next)
      return
    }

    if (chars.length > 1) {
      // Paste into this box (or autofill of multiple chars)
      const next = [...digits]
      chars.forEach((char, offset) => {
        if (index + offset < CODE_LENGTH) {
          next[index + offset] = char
        }
      })
      commitDigits(next)
      focusIndex(Math.min(index + chars.length, CODE_LENGTH - 1))
      return
    }

    const next = [...digits]
    next[index] = chars[0]
    commitDigits(next)
    if (index < CODE_LENGTH - 1) {
      focusIndex(index + 1)
    }
  }

  const handleKeyDown = (index: number, event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Backspace') {
      event.preventDefault()
      if (digits[index]) {
        const next = [...digits]
        next[index] = ''
        commitDigits(next)
      } else if (index > 0) {
        const next = [...digits]
        next[index - 1] = ''
        commitDigits(next)
        focusIndex(index - 1)
      }
      return
    }

    if (event.key === 'ArrowLeft') {
      event.preventDefault()
      focusIndex(index - 1)
      return
    }

    if (event.key === 'ArrowRight') {
      event.preventDefault()
      focusIndex(index + 1)
      return
    }

    if (event.key === 'Enter') {
      event.preventDefault()
      onCompleteEnter?.()
      return
    }

    // Block non-code keys except modifiers / navigation already handled
    if (
      event.key.length === 1 &&
      !event.ctrlKey &&
      !event.metaKey &&
      !event.altKey &&
      !CODE_CHAR_PATTERN.test(event.key.toUpperCase())
    ) {
      event.preventDefault()
    }
  }

  const handlePaste = (index: number, event: React.ClipboardEvent<HTMLInputElement>) => {
    event.preventDefault()
    const pasted = sanitizeCodeChars(event.clipboardData.getData('text'))
    if (pasted.length === 0) return

    const next = [...digits]
    pasted.forEach((char, offset) => {
      if (index + offset < CODE_LENGTH) {
        next[index + offset] = char
      }
    })
    commitDigits(next)
    focusIndex(Math.min(index + pasted.length, CODE_LENGTH - 1))
  }

  return (
    <Box
      role="group"
      aria-label="Owner override code"
      sx={{
        display: 'flex',
        justifyContent: 'center',
        gap: { xs: 1, sm: 1.25 },
        my: 1,
      }}
    >
      {digits.map((digit, index) => (
        <Box
          key={index}
          component="input"
          ref={(element: HTMLInputElement | null) => {
            inputRefs.current[index] = element
          }}
          type="text"
          inputMode="text"
          autoComplete={index === 0 ? 'one-time-code' : 'off'}
          autoCapitalize="characters"
          spellCheck={false}
          maxLength={1}
          value={digit}
          disabled={disabled}
          aria-label={`Code digit ${index + 1}`}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
            handleChange(index, event.target.value)
          }
          onKeyDown={(event: React.KeyboardEvent<HTMLInputElement>) =>
            handleKeyDown(index, event)
          }
          onPaste={(event: React.ClipboardEvent<HTMLInputElement>) => handlePaste(index, event)}
          onFocus={(event: React.FocusEvent<HTMLInputElement>) => event.target.select()}
          sx={{
            width: { xs: 40, sm: 48 },
            height: { xs: 48, sm: 56 },
            textAlign: 'center',
            fontSize: { xs: '1.25rem', sm: '1.5rem' },
            fontWeight: 700,
            letterSpacing: 0,
            fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
            borderRadius: 1.5,
            border: '2px solid',
            borderColor: error ? 'error.main' : digit ? 'primary.main' : 'divider',
            bgcolor: 'background.paper',
            color: 'text.primary',
            outline: 'none',
            transition: (theme) =>
              theme.transitions.create(['border-color', 'box-shadow'], {
                duration: theme.transitions.duration.shorter,
              }),
            '&:focus': {
              borderColor: error ? 'error.main' : 'primary.main',
              boxShadow: (theme) =>
                `0 0 0 3px ${
                  error ? theme.palette.error.main : theme.palette.primary.main
                }33`,
            },
            '&:disabled': {
              opacity: 0.6,
              cursor: 'not-allowed',
            },
          }}
        />
      ))}
    </Box>
  )
}

const PendingSaleNotPurchasedDialog: React.FC<PendingSaleNotPurchasedDialogProps> = ({
  open,
  onClose,
  vehicles,
  onUnlocked,
  onNotify,
}) => {
  const { showError, showInfo, showSuccess } = useSnackbar()
  const {
    code: expectedCode,
    isLoading: isLoadingCode,
    consumeCode,
    isConsuming,
  } = usePendingSaleOverrideSettings({
    dontShowUpdates: true,
    enabled: open,
  })
  const [enteredCode, setEnteredCode] = useState('')
  const [codeError, setCodeError] = useState<string | null>(null)
  const [isNotifying, setIsNotifying] = useState(false)
  const [focusRequestId, setFocusRequestId] = useState(0)

  useEffect(() => {
    if (!open) {
      setEnteredCode('')
      setCodeError(null)
      setIsNotifying(false)
    }
  }, [open])

  const stockLabels = vehicles
    .map((vehicle) => vehicle.stock_number || vehicle.id)
    .filter(Boolean)
    .join(', ')

  const alreadyNotified =
    vehicles.length > 0 &&
    vehicles.every((vehicle) => vehicle.pending_sale_not_purchased_notify === true)

  const handleUnlock = async () => {
    const normalizedEntered = enteredCode.trim().toUpperCase()
    const normalizedExpected = (expectedCode || '').trim().toUpperCase()

    if (normalizedEntered.length < CODE_LENGTH) {
      setCodeError('Incorrect code. Ask an owner for the code shown on the Sales tab.')
      return
    }

    if (!normalizedExpected) {
      setCodeError('Override code is not available yet. Ask an owner to open the Sales tab.')
      return
    }

    try {
      await consumeCode(normalizedEntered)
      setCodeError(null)
      onUnlocked()
    } catch (error) {
      if (error instanceof PendingSaleOverrideConsumeError && error.reason === 'missing') {
        setCodeError('Override code is not available yet. Ask an owner to open the Sales tab.')
        return
      }
      if (error instanceof PendingSaleOverrideConsumeError && error.reason === 'mismatch') {
        setCodeError(
          normalizedEntered === normalizedExpected
            ? 'That code was already used. Ask an owner for the new code on the Sales tab.'
            : 'Incorrect code. Ask an owner for the code shown on the Sales tab.',
        )
        return
      }
      console.error('Failed to consume pending sale override code:', error)
      setCodeError('Could not apply that code. Try again, or ask an owner for the Sales tab code.')
    }
  }

  const handleNotify = async () => {
    if (vehicles.length === 0) {
      showError('No vehicles selected')
      return
    }

    if (alreadyNotified) {
      showInfo('Notification already sent')
      return
    }

    try {
      setIsNotifying(true)
      await onNotify(vehicles)
      showSuccess(
        vehicles.length === 1
          ? 'Pending sale not purchased notification sent'
          : `Pending sale not purchased notification sent for ${vehicles.length} trucks`,
      )
      onClose()
    } catch (error) {
      console.error('Failed to send pending sale not purchased notification:', error)
      showError('Failed to send notification')
    } finally {
      setIsNotifying(false)
    }
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      disableAutoFocus
      TransitionProps={{
        onEntered: () => {
          setFocusRequestId((current) => current + 1)
        },
      }}
    >
      <DialogTitle>Pending sale not purchased</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>
          This truck has not been purchased for inventory. You need permission to mark it as
          Pending Sale, or an owner override code from the Sales tab on the owner dashboard.
        </DialogContentText>
        {stockLabels ? (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Truck{vehicles.length === 1 ? '' : 's'}: {stockLabels}
          </Typography>
        ) : null}
        <Typography variant="subtitle2" sx={{ mb: 0.5, textAlign: 'center' }}>
          Owner override code
        </Typography>
        <OverrideCodeBoxes
          value={enteredCode}
          onChange={(next) => {
            setEnteredCode(next)
            if (codeError) setCodeError(null)
          }}
          disabled={isLoadingCode || isConsuming}
          error={Boolean(codeError)}
          focusRequestId={focusRequestId}
          onCompleteEnter={handleUnlock}
        />
        <FormHelperText error={Boolean(codeError)} sx={{ textAlign: 'center', mx: 0 }}>
          {codeError || 'Ask an owner for the 6-character code shown on their Sales tab.'}
        </FormHelperText>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, gap: 1, flexWrap: 'wrap' }}>
        <Button onClick={onClose} color="inherit">
          Cancel
        </Button>
        <Button
          onClick={handleNotify}
          color="warning"
          variant="outlined"
          disabled={isNotifying || isConsuming || alreadyNotified || vehicles.length === 0}
        >
          {alreadyNotified ? 'Notification already sent' : isNotifying ? 'Notifying…' : 'Notify'}
        </Button>
        <Button
          onClick={handleUnlock}
          color="primary"
          variant="contained"
          disabled={isLoadingCode || isConsuming || enteredCode.length < CODE_LENGTH}
        >
          Continue with code
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default PendingSaleNotPurchasedDialog
